import { useCallback, useEffect, useRef, useState } from "react";
import { Calculator, Eye, ImagePlus, Medal, Pencil, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { RankCostModal, RankProfitModal } from "./AdminRankCostProfit";

const EMPTY_FORM = {
  name: "",
  level: "",
  description: "",
  image_url: "",
  active: true,
  requirement: 1,
};

export default function AdminRanks() {
  const [ranks, setRanks] = useState(null);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit', id?, form}
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [costOpen, setCostOpen] = useState(false);
  const [profitOpen, setProfitOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/ranks").then((res) => setRanks(res.data)).catch(() => setRanks([]));
  }, []);

  useEffect(load, [load]);

  const openCreate = () => {
    setError("");
    const maxLevel = ranks?.length ? Math.max(...ranks.map((r) => r.level)) : 0;
    setModal({ mode: "create", form: { ...EMPTY_FORM, level: maxLevel + 1 } });
  };

  const openEdit = (rank) => {
    setError("");
    setModal({
      mode: "edit",
      id: rank.id,
      form: {
        name: rank.name,
        level: rank.level,
        description: rank.description || "",
        image_url: rank.image_url || "",
        active: rank.active,
        requirement: rank.requirement ?? 1,
      },
    });
  };

  const setField = (key) => (e) =>
    setModal((m) => ({ ...m, form: { ...m.form, [key]: e.target.value } }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/uploads/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setModal((m) => ({ ...m, form: { ...m.form, image_url: data.url } }));
      toast.success("Image uploaded");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async () => {
    const { mode, id, form } = modal;
    setLoading(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        level: Number(form.level),
        description: form.description,
        image_url: form.image_url || null,
        active: form.active,
        requirement: Number(form.requirement) || 1,
      };
      if (mode === "create") {
        await api.post("/admin/ranks", payload);
        toast.success("Rank created");
      } else {
        await api.put(`/admin/ranks/${id}`, payload);
        toast.success("Rank updated");
      }
      setModal(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleRank = async (rank) => {
    setBusyId(rank.id);
    try {
      await api.put(`/admin/ranks/${rank.id}`, {
        name: rank.name,
        level: rank.level,
        description: rank.description,
        image_url: rank.image_url,
        active: !rank.active,
        requirement: rank.requirement,
      });
      toast.success(`Rank ${rank.active ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (rank) => {
    setBusyId(rank.id);
    try {
      const { data } = await api.delete(`/admin/ranks/${rank.id}`);
      toast.success(data.message);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setConfirmDelete(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-ranks-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="eyebrow-label mb-2">Configuration</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Rank Management</h1>
          <p className="mt-2 text-sm text-[#64748B]">
            The central PLUSMAKER rank structure. Changes apply across the entire ecosystem instantly.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button onClick={() => setCostOpen(true)} className="btn-outline-gold px-5 py-2.5 text-sm flex items-center gap-1.5" data-testid="rank-cost-open-button">
            <Calculator className="h-4 w-4" /> Cost
          </button>
          <button onClick={() => setProfitOpen(true)} className="btn-outline-gold px-5 py-2.5 text-sm flex items-center gap-1.5" data-testid="rank-profit-open-button">
            <TrendingUp className="h-4 w-4" /> Profit
          </button>
          <button onClick={openCreate} className="btn-gold px-6 py-2.5 text-sm" data-testid="rank-create-open-button">
            + Create Rank
          </button>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-ranks-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Rank</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Level</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden xl:table-cell">Requirement</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Description</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Members</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {ranks === null ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#64748B]">Loading ranks...</td></tr>
              ) : ranks.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#64748B]" data-testid="admin-ranks-empty">No ranks configured.</td></tr>
              ) : (
                ranks.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-rank-row-${r.level}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {r.image_url ? (
                          <img src={r.image_url} alt={r.name} className="h-10 w-10 rounded-lg object-cover border border-[#222530] shrink-0" loading="lazy" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
                            <Medal className="h-4 w-4 text-[#D4AF37]" />
                          </div>
                        )}
                        <p className="text-white font-medium">{r.name}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-[#D4AF37]">{r.level}</td>
                    <td className="px-5 py-3 font-mono text-white hidden xl:table-cell" data-testid={`admin-rank-requirement-${r.level}`}>{r.requirement ?? 1}</td>
                    <td className="px-5 py-3 text-[#94A3B8] text-xs hidden lg:table-cell max-w-[260px] truncate">{r.description || "—"}</td>
                    <td className="px-5 py-3 font-mono text-white hidden sm:table-cell">{r.member_count}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        r.active ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-[#64748B] border-[#222530]"
                      }`} data-testid={`admin-rank-status-${r.level}`}>
                        {r.active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => setPreview(r)} className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-rank-preview-${r.level}`}>
                          <Eye className="h-3 w-3" /> View
                        </button>
                        <button onClick={() => openEdit(r)} className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-rank-edit-${r.level}`}>
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => toggleRank(r)} disabled={busyId === r.id} className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border transition-colors disabled:opacity-50 ${
                          r.active ? "text-[#94A3B8] border-[#222530] hover:bg-[#121318]" : "text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"
                        }`} data-testid={`admin-rank-toggle-${r.level}`}>
                          {r.active ? "Deactivate" : "Activate"}
                        </button>
                        {confirmDelete === r.id ? (
                          <button onClick={() => handleDelete(r)} disabled={busyId === r.id} className="text-xs font-medium rounded-lg px-2.5 py-1.5 border text-red-400 border-red-400/50 bg-red-400/10 transition-colors disabled:opacity-50" data-testid={`admin-rank-delete-confirm-${r.level}`}>
                            {busyId === r.id ? "..." : "Confirm"}
                          </button>
                        ) : (
                          <button onClick={() => { setConfirmDelete(r.id); setTimeout(() => setConfirmDelete(null), 4000); }} className="text-xs text-red-400/80 hover:text-red-400 border border-[#222530] hover:border-red-400/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-rank-delete-${r.level}`}>
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {costOpen && <RankCostModal onClose={() => setCostOpen(false)} />}
      {profitOpen && <RankProfitModal onClose={() => setProfitOpen(false)} />}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="rank-form-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setModal(null)} />
          <div className="card-lux relative w-full max-w-md p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">{modal.mode === "create" ? "Create Rank" : "Edit Rank"}</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">
              {modal.mode === "create" ? "New Rank" : modal.form.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Rank Name</label>
                <input className="input-lux" value={modal.form.name} onChange={setField("name")} placeholder="e.g. Rookie Trader" data-testid="rank-form-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Level / Display Order</label>
                <input type="number" min="1" className="input-lux font-mono" value={modal.form.level} onChange={setField("level")} data-testid="rank-form-level-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Progression Requirement <span className="text-[#64748B]">(admin-only)</span>
                </label>
                <input type="number" min="0.0001" step="any" className="input-lux font-mono" value={modal.form.requirement} onChange={setField("requirement")} data-testid="rank-form-requirement-input" />
                <p className="mt-1.5 text-[10px] text-[#64748B]">Private progress needed to reach this rank. Never shown to members.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Description</label>
                <textarea rows={2} className="input-lux resize-y" value={modal.form.description} onChange={setField("description")} placeholder="What this rank represents" data-testid="rank-form-description-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Rank Image / Icon</label>
                <div className="flex items-start gap-4">
                  {modal.form.image_url ? (
                    <img src={modal.form.image_url} alt="Rank" className="h-16 w-16 rounded-lg object-cover border border-[#D4AF37]/30 shrink-0" data-testid="rank-form-image-preview" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-[#0B0C10] border border-dashed border-[#222530] flex items-center justify-center shrink-0">
                      <ImagePlus className="h-5 w-5 text-[#64748B]" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input className="input-lux font-mono text-xs" value={modal.form.image_url} onChange={setField("image_url")} placeholder="Image URL or upload →" data-testid="rank-form-image-url-input" />
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="block w-full text-xs text-[#94A3B8] file:mr-3 file:rounded-lg file:border file:border-[#D4AF37]/40 file:bg-[#D4AF37]/10 file:px-3 file:py-1.5 file:text-xs file:text-[#D4AF37] hover:file:bg-[#D4AF37]/20" data-testid="rank-form-image-upload" />
                    {uploading && <p className="text-xs text-[#D4AF37]">Uploading...</p>}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Status</label>
                <select className="input-lux" value={modal.form.active ? "active" : "inactive"} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, active: e.target.value === "active" } }))} data-testid="rank-form-status-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              {error && (
                <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="rank-form-error">{error}</p>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={handleSave} disabled={loading || uploading} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="rank-form-save-button">
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={() => setModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="rank-form-cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="rank-preview-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreview(null)} />
          <div className="relative w-full max-w-sm">
            <p className="eyebrow-label mb-3 text-center">Rank Preview</p>
            <div className="card-lux p-8 text-center">
              {preview.image_url ? (
                <img src={preview.image_url} alt={preview.name} className="h-20 w-20 rounded-xl object-cover border border-[#D4AF37]/30 mx-auto mb-5" />
              ) : (
                <div className="h-20 w-20 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center mx-auto mb-5">
                  <Medal className="h-8 w-8 text-[#D4AF37]" />
                </div>
              )}
              <p className="font-heading text-xl font-bold text-gold-gradient">{preview.name}</p>
              <p className="font-mono text-xs text-[#64748B] mt-1.5">Level {preview.level}</p>
              {preview.description && (
                <p className="text-sm text-[#94A3B8] mt-4 leading-relaxed">{preview.description}</p>
              )}
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mt-5">
                {preview.member_count} member{preview.member_count === 1 ? "" : "s"} at this rank
              </p>
            </div>
            <button onClick={() => setPreview(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-4" data-testid="rank-preview-close-button">Close Preview</button>
          </div>
        </div>
      )}
    </div>
  );
}
